import React, { useState, useRef, useEffect, useCallback } from 'react';
import { InfiniteScroll } from '@quintype/components';
import wretch from 'wretch';
import get from 'lodash/get';
import concat from 'lodash/concat';
import PropTypes from 'prop-types';
import { ListStory } from '../../molecules/list-story';
import { sortStoriesByLastUpdatedAt } from '../../../helpers/utils';
import { contentClick } from '../../../helpers/datalayer-utils';
import { useSelector } from 'react-redux';

const InfiniteStoryList = ({
  stories,
  showVideo,
  fullWidthResult,
  hostImage,
  sectionHeading,
  hideAuthor,
  fetchCollectionStories,
  baseApi,
  pagetype,
  searchTerm,
}) => {
  const [offset, setOffset] = useState(0);
  const [limit] = useState(20);
  const [storyList, setStoryList] = useState([]);

  const userData = useSelector((state) => get(state, ['userData']));
  const pageType = useSelector((state) => get(state, ['qt', 'pageType']));
  const isFetching = useRef(false);

  const loadMore = useCallback(() => {
    if (isFetching.current) return; // Prevent multiple concurrent requests
    isFetching.current = true;

    const storyFields =
      'slug,story-content-id,id,headline,metadata,hero-image-s3-key,hero-image-metadata,summary,sections,tags,author-name,author-id,authors,created-at,first-published-at,published-at,last-published-at,story-template,subheadline';
    const url = `${baseApi}&offset=${offset}&limit=${limit}&fields=${storyFields}`;
    wretch()
      .url(url)
      .get()
      .json((results) => {
        const resultsData = fetchCollectionStories
          ? { stories: get(results, 'items').map((item) => item.story) }
          : get(results, 'results', results);
        const filteredStories =
          pagetype === 'search-page'
            ? resultsData.stories.filter((story) => {
                const headlineMatch = story?.headline
                  ?.toLowerCase()
                  ?.includes(searchTerm?.toLowerCase());
                const authorMatch = get(story, ['author-name'])
                  ?.toLowerCase()
                  ?.includes(searchTerm?.toLowerCase());
                return headlineMatch || authorMatch;
              })
            : resultsData.stories.filter(
                (story) =>
                  !['fillers', 'filler'].includes(
                    get(story, ['metadata', 'story-attributes', 'fillers', 0], ''),
                  ) &&
                  !['fillers', 'filler'].includes(
                    get(story, ['metadata', 'story-attributes', 'syndicatedfrom', 0]),
                  ),
              );

        setStoryList((prevStories) => {
          const uniqueStories = [
            ...prevStories,
            ...filteredStories.filter(
              (story) => !prevStories.some((prevStory) => prevStory.id === story.id),
            ),
          ];
          return uniqueStories;
        });
        setOffset((prevOffset) => prevOffset + limit);
      })
      .finally(() => {
        isFetching.current = false;
      });
  }, [baseApi, offset, limit, fetchCollectionStories, pagetype, searchTerm]);

  const itemPageBase = (story) => {
    const isVideo =
      (story['story-template'] === 'video' || story['story-template'] === 'ndtv-video') &&
      showVideo;
    return (
      <div onClick={() => contentClick(userData, story, pageType)}>
        <ListStory
          story={story}
          showVideo={isVideo}
          fullWidthResult={fullWidthResult}
          hostImage={hostImage}
          sectionHeading={sectionHeading}
          hideAuthor={hideAuthor}
        />
      </div>
    );
  };

  const sortedStories =
    storyList?.map((story, index) => ({
      ...story,
      storyIndex: index,
    })) || [];

  useEffect(() => {
    if (stories) {
      setStoryList(stories);
    }
  }, [stories]);

  return stories ? (
    <InfiniteScroll
      render={itemPageBase}
      items={pagetype !== 'search-page' ? sortStoriesByLastUpdatedAt(sortedStories) : sortedStories}
      loadNext={loadMore}
      focusCallbackAt={20}
      onFocus={(index) => index}
    />
  ) : null;
};

InfiniteStoryList.propTypes = {
  stories: PropTypes.array,
  showVideo: PropTypes.bool,
  fullWidthResult: PropTypes.bool,
  hostImage: PropTypes.string,
  sectionHeading: PropTypes.string,
  hideAuthor: PropTypes.bool,
  fetchCollectionStories: PropTypes.bool,
  baseApi: PropTypes.string,
  pagetype: PropTypes.string,
  searchTerm: PropTypes.string,
};

export { InfiniteStoryList };
